import React, { useState, useMemo, useEffect, useRef } from "react";
import { motion, AnimatePresence } from 'framer-motion';
import { 
  TrendingUp, Award, ThumbsUp, GitMerge, Sliders, 
  Gauge, Users, MessageSquare, Compass, Filter, ArrowUpDown 
} from 'lucide-react';

import PartyLogo from "../../assets/Libby_Orange.png";
import { getStorageURL } from "../../firebase";
import { StatCard, LABEL, reliabilityLabel, WardPerformanceCard } from './CandidateShared';

// --- IMPORT TABS ---
import MissionControlTab from './MissionControlTab';
import ElectorateTab from './ElectorateTab';
import MessagingTab from './MessagingTab';

export default function CandidateDashboard({data, campaignId}) {
  // --- STATE ---
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedWardCode, setSelectedWardCode] = useState(null);
  const [isCoachView, setIsCoachView] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'fit', direction: 'desc' });
  const [imageURL, setImageURL] = useState("");
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    const getImage = () => getStorageURL(`/campaigns/${campaignId}/candidate_image`).then((value) => setImageURL(value));
    getImage();
  }, [campaignId]);

  // --- DATA DESTRUCTURING (ROBUST) ---
  const payload = data?.minerva_analysis || {};
  const profile = payload.candidate_profile || {};
  const peer = payload.peer_assessment_summary || {};
  const performance = payload.performance_prediction || {};
  const strengths = payload.strengths_analysis || {};
  const development = payload.development_priorities || [];
  const narrative = payload.narrative_recommendations_ovid || {};
  const northStarNarrative = payload.north_star_narrative || {};
  const synthesis = payload.candidate_synthesis || {};
  const { resilience_persona, personality_persona, communication_persona } = synthesis;
  
  // --- ROBUST EXTRACTION: HYBRID INTELLIGENCE ---
  // This helper finds the hybrid array even if the data structure shifts slightly
  const getHybridAnalysis = (source) => {
    const minerva = source?.minerva_analysis || {};
    
    // 1. Direct location (Standard)
    if (minerva.hybrid_ward_analysis) return minerva.hybrid_ward_analysis;
    
    // 2. Nested in Theia (Common alternate path)
    if (minerva.theia_electorate_analysis?.hybrid_ward_analysis) return minerva.theia_electorate_analysis.hybrid_ward_analysis;

    // 3. Nested in Ward Strategy (Edge case)
    if (minerva.ward_targeting_strategy_ovid?.hybrid_ward_analysis) return minerva.ward_targeting_strategy_ovid.hybrid_ward_analysis;

    // 4. Root level (If structure is flattened)
    if (source?.hybrid_ward_analysis) return source.hybrid_ward_analysis;

    return [];
  };

  const hybrid_ward_analysis = useMemo(() => getHybridAnalysis(data), [data]);
  const general_archetype_fit = payload.general_archetype_fit || {};

  const wardStrategy = payload.ward_targeting_strategy_ovid || {};
  const authenticityAnalysis = payload.strategic_authenticity_analysis?.ward_analysis || [];
  const theiaElectorateAnalysis = payload.theia_electorate_analysis || {};
  const authenticityGapAnalysis = payload.authenticity_gap_analysis || {};
  const compatibility = payload.archetype_compatibility || {};
  const theiaWardScores = theiaElectorateAnalysis.ward_specificity_analysis?.ward_compatibility_scores || [];
  
  // --- DERIVED DATA & LOGIC ---
  const perfPrediction = performance.performance_prediction || {};
  const strategicAdvantages = strengths.strategic_advantages || [];
  const wardCompatAdvantage = strategicAdvantages.find(a => a.advantage === 'Ward Compatibility');
  const overallWardCompatScore = wardCompatAdvantage ? wardCompatAdvantage.score : null;

  // Header Labels
  const expectedPerfLabel = LABEL(perfPrediction.expected_performance ?? null);
  const overallFitLabel = LABEL(overallWardCompatScore ?? null); 
  const strengthScoreLabel = LABEL(strengths.overall_strength_score ?? null);
  const peerConsensusLabel = reliabilityLabel(peer.consensus_score ?? null);

  // --- FIXED WARD SORTING LOGIC ---
  // Now includes defensive_wards to ensure all 31 wards are passed to MissionControlTab
  const allTargetWardsCombined = useMemo(() => [
    ...(wardStrategy.priority_wards || []),
    ...(wardStrategy.opportunity_wards || []),
    ...(wardStrategy.defensive_wards || []), // Included defensive/base-maintenance wards
  ], [
    wardStrategy.priority_wards, 
    wardStrategy.opportunity_wards, 
    wardStrategy.defensive_wards
  ]);

  const allTargetWards = useMemo(() => {
    let wards = [...allTargetWardsCombined];
    wards.sort((a, b) => {
      let valA = 0, valB = 0;
      switch (sortConfig.key) {
        case 'fit': valA = a?.scoring_factors?.minerva_candidate_compatibility ?? 0; valB = b?.scoring_factors?.minerva_candidate_compatibility ?? 0; break;
        case 'win': valA = a?.scoring_factors?.artemis_winability_score ?? 0; valB = b?.scoring_factors?.artemis_winability_score ?? 0; break;
        case 'priority': valA = a?.combined_priority_score ?? 0; valB = b?.combined_priority_score ?? 0; break;
        default: break;
      }
      return sortConfig.direction === 'desc' ? valB - valA : valA - valB;
    });
    return wards;
  }, [allTargetWardsCombined, sortConfig]);

  // Selection Logic
  const handleWardClick = (ward) => setSelectedWardCode(ward.ward_code === selectedWardCode ? null : ward.ward_code);
  const selectedWardStrategy = allTargetWards.find(w => w.ward_code === selectedWardCode);
  const selectedWardName = selectedWardStrategy?.ward_name;
  const selectedWardNarrative = (narrative.ward_specific_messaging || []).find(w => w.ward_name === selectedWardName);
  const selectedWardRisk = (payload.contextual_risk_analysis?.ward_specific_risks || []).find(w => w.ward_name === selectedWardName);
  const selectedWardTheiaScore = theiaWardScores.find(w => w.ward_code === selectedWardCode);

  // Priority Alerts Engine
  const generatePriorityAlerts = () => {
    const alerts = [];
    const priorityWard = (wardStrategy.priority_wards || [])[0];
    if (priorityWard) {
      const auth = authenticityAnalysis.find(a => a.ward_name === priorityWard.ward_name);
      const highRiskMismatches = (auth?.authenticity_analysis || []).filter(a => a.compatibility === 'High-Risk Mismatch');
      if (highRiskMismatches.length > 0) {
        const mismatchDomains = highRiskMismatches.map(m => m.domain).join(' & ');
        alerts.push({ type: 'danger', message: `Your primary target (${priorityWard.ward_name}) has critical authenticity mismatches on "${mismatchDomains}". All messaging must use the recommended pivots. This is non-negotiable.` });
      }
    }
    const opportunityWards = wardStrategy.opportunity_wards || [];
    for (const oppWard of opportunityWards) {
        const auth = authenticityAnalysis.find(a => a.ward_name === oppWard.ward_name);
        const hasSynergy = (auth?.authenticity_analysis || []).some(a => a.compatibility === 'Excellent Synergy');
        const hasHighRisk = (auth?.authenticity_analysis || []).some(a => a.compatibility === 'High-Risk Mismatch');
        const politicalTask = auth?.ward_blueprint?.political_task;
        if (hasSynergy && !hasHighRisk && politicalTask === 'Mobilization') {
            alerts.push({ type: 'success', message: `The "${oppWard.ward_name}" ward is an ideal, low-energy mobilization target. The candidate's natural profile is a perfect fit. This is a prime target for a data-driven, volunteer-led GOTV push.` });
            break;
        }
    }
    const devPriority = development.find(d => d.area === 'Message Adaptation');
    const perfOpportunity = (performance.improvement_opportunities || []).some(o => o.area === 'Message Adaptation');
    const hasMismatch = authenticityAnalysis.some(w => w.authenticity_analysis?.some(a => (a.domain === 'Cognitive Style' || a.domain.includes('Moral Frame')) && a.compatibility.includes('Mismatch')));
    if (devPriority && perfOpportunity && hasMismatch) {
      alerts.push({ type: 'warning', message: `"Message Adaptation" is the single highest-impact development area, confirmed by three separate analysis modules. Coaching on "fact-based storytelling" is the top training priority.` });
    }
    return alerts;
  };
  const priorityAlerts = generatePriorityAlerts();

  return (
    <div className="min-h-screen text-slate-300 p-3 sm:p-6 lg:p-10 font-sans">
        
        {/* --- GLOBAL HEADER --- */}
        <header className="mb-8 pb-6">
          <div className="flex flex-col md:flex-row md:items-stretch md:justify-between mb-6 gap-4">
            <div className="flex items-stretch space-x-6">
              <div className="w-20 sm:w-28 md:w-36 aspect-[4/5] bg-slate-800 rounded-lg overflow-hidden border-2 border-slate-600 flex-shrink-0">
                <img 
                  src={imageURL || `https://api.dicebear.com/7.x/avataaars/svg?seed=${profile.name || 'Candidate'}`} 
                  alt={profile.name} 
                  className="w-full h-full object-cover" 
                  onError={(e) => { e.target.src = `https://api.dicebear.com/7.x/avataaars/svg?seed=${profile.name || 'Candidate'}`; }} 
                />
              </div>
              <div className="flex flex-col justify-between flex-1 py-1">
                <div>
                  <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white leading-tight mb-1">{profile.name || "Candidate Profile"}</h1>
                  <p className="text-blue-400 text-sm sm:text-lg mb-2 sm:mb-4">{profile.experience_level || "Political Candidate"}</p>
                </div>
                <div className="max-w-4xl hidden sm:block">
                  <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-2">Core Beliefs</h3>
                  <p className="text-slate-300 leading-relaxed line-clamp-3">{profile.core_beliefs || "No core beliefs statement available."}</p>
                </div>
              </div>
            </div>
            
            <div className="flex flex-row md:flex-col items-center md:items-end justify-between md:justify-between py-1 gap-3 md:gap-0 w-full md:w-auto">
              <div className="w-14 h-14 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                <img src={PartyLogo} alt="Party Logo" className="w-full h-full object-contain" />
              </div>
              <button 
                onClick={() => setIsCoachView(prev => !prev)}
                className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-xs font-medium border transition-all ${isCoachView ? 'bg-purple-500/10 text-purple-400 border-purple-500/20' : 'bg-slate-700/50 text-slate-400 border-slate-700 hover:border-slate-500'}`}
              >
                <Sliders size={14} /><span>{isCoachView ? "Coach View ON" : "CoachView OFF"}</span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            <StatCard title="Expected Performance" value={expectedPerfLabel} icon={TrendingUp} colorClass="text-indigo-400" />
            <StatCard title="Overall Electorate Fit" value={overallFitLabel} icon={GitMerge} colorClass="text-green-400" />
            <StatCard title="Strategic Strength" value={strengthScoreLabel} icon={Award} colorClass="text-yellow-400" />
            <StatCard title="Peer Consensus" value={peerConsensusLabel} icon={ThumbsUp} colorClass="text-blue-400" />
          </div>
        </header>

        {/* --- GLOBAL WARD TARGETING COMMAND CENTER --- */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.3, delay: 0.1 }}
          className="mb-8"
        >
          <div className="flex flex-wrap items-center justify-between mb-4 gap-4">
            <div className="flex items-center space-x-3">
              <Compass className="text-accent-400" size={20} />
              <h3 className="text-base sm:text-xl font-bold text-white">Ward Targeting Command Center</h3>
            </div>
            <div className="flex items-center space-x-2">
               <div className="relative">
                 <Filter size={14} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                 <select
                   className="pl-9 pr-8 py-1.5 bg-slate-800 border border-slate-600 rounded-lg text-xs text-slate-200 focus:outline-none focus:border-accent-400 appearance-none cursor-pointer"
                   value={`${sortConfig.key}-${sortConfig.direction}`}
                   onChange={(e) => {
                     const [key, direction] = e.target.value.split('-');
                     setSortConfig({ key, direction });
                   }}
                 >
                   <option value="fit-desc">Candidate Fit: High to Low</option>
                   <option value="fit-asc">Candidate Fit: Low to High</option>
                   <option value="win-desc">Winability: High to Low</option>
                   <option value="win-asc">Winability: Low to High</option>
                   <option value="priority-desc">Priority: High to Low</option>
                   <option value="priority-asc">Priority: Low to High</option>
                 </select>
                 <ArrowUpDown size={14} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 pointer-events-none" />
               </div>
            </div>
          </div>
          
          {allTargetWards && allTargetWards.length > 0 ? (
            <div 
              ref={scrollContainerRef}
              className="flex space-x-3 sm:space-x-4 overflow-x-auto px-3 sm:px-6 py-4 pb-6 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-800"
            >
              {allTargetWards.map((ward) => (
                <WardPerformanceCard 
                  key={ward.ward_code} 
                  ward={ward} 
                  isSelected={selectedWardCode === ward.ward_code}
                  onClick={handleWardClick} 
                />
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-24 rounded-lg bg-slate-800/50 text-slate-500 border border-slate-700">
              <p>No ward targeting data available for this candidate.</p>
            </div>
          )}
        </motion.div>

        {/* --- DANTE WHITE GLOW TAB NAVIGATION --- */}
        <div className="flex items-center space-x-3 sm:space-x-6 md:space-x-8 mb-6 sm:mb-8 border-b border-slate-700/50 relative overflow-x-auto scrollbar-none">
            {[
                { id: 'overview', label: 'Mission Control', icon: Gauge },
                { id: 'electorate', label: 'Electorate', icon: Users },
                { id: 'messaging', label: 'Messaging Engine', icon: MessageSquare }
            ].map((tab) => {
                const isActive = activeTab === tab.id;
                const Icon = tab.icon;
                return (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`relative py-4 px-2 flex items-center space-x-2 text-xs md:text-sm font-bold uppercase tracking-widest transition-all duration-300 outline-none group ${isActive ? 'text-white' : 'text-slate-500 hover:text-slate-300'}`}
                    >
                        <Icon size={16} className={`transition-all duration-300 ${isActive ? "text-white drop-shadow-[0_0_10px_rgba(255,255,255,0.8)]" : "group-hover:text-slate-300"}`} />
                        <span className={`hidden md:block ${isActive ? "drop-shadow-[0_0_10px_rgba(255,255,255,0.6)]" : ""}`}>{tab.label}</span>
                        <span className={`md:hidden ${isActive ? "drop-shadow-[0_0_10px_rgba(255,255,255,0.6)]" : ""}`}>{tab.label === 'Mission Control' ? 'Overview' : tab.label.split(' ')[0]}</span>
                        {isActive && (
                            <>
                                <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-[2px] bg-white shadow-[0_0_10px_1px_rgba(255,255,255,0.5)]" initial={false} transition={{ type: "spring", stiffness: 500, damping: 30 }} />
                                <div className="absolute inset-0 bg-gradient-to-t from-white/5 to-transparent opacity-20 blur-md -z-10" /> 
                            </>
                        )}
                    </button>
                );
            })}
        </div>

        {/* --- MAIN CONTENT AREA --- */}
        <AnimatePresence mode="wait">
            <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }}>
                
                {activeTab === 'overview' && (
                  <MissionControlTab 
                    priorityAlerts={priorityAlerts}
                    performance={performance}
                    profile={profile}
                    resilience_persona={resilience_persona}
                    personality_persona={personality_persona}
                    allTargetWards={allTargetWards}
                    theiaWardScores={theiaWardScores}
                    development={development}
                    peer={peer}
                  />
                )}

                {activeTab === 'electorate' && (
                  <ElectorateTab 
                    selectedWardCode={selectedWardCode}
                    selectedWardName={selectedWardName}
                    selectedWardStrategy={selectedWardStrategy}
                    selectedWardRisk={selectedWardRisk}
                    selectedWardTheiaScore={selectedWardTheiaScore}
                    selectedWardNarrative={selectedWardNarrative}
                    profile={profile}
                    theiaElectorateAnalysis={theiaElectorateAnalysis}
                    authenticityGapAnalysis={authenticityGapAnalysis}
                    compatibility={compatibility}
                    generalArchetypeFit={general_archetype_fit} 
                    hybridWardAnalysis={hybrid_ward_analysis} 
                  />
                )}

                {activeTab === 'messaging' && (
                  <MessagingTab 
                    selectedWardCode={selectedWardCode}
                    selectedWardName={selectedWardName}
                    selectedWardNarrative={selectedWardNarrative}
                    selectedWardRisk={selectedWardRisk}
                    profile={profile}
                    isCoachView={isCoachView}
                    northStarNarrative={northStarNarrative}
                    narrative={narrative}
                    communication_persona={communication_persona}
                  />
                )}

            </motion.div>
        </AnimatePresence>
    </div>
  );
}